/**
 * 인터뷰 준비 케이스 — `/admin/geo-cases` (261023-geo-watch-agent).
 *
 * 개선 에이전트가 「인용된 우리 랭킹 페이지에서 고객 순위가 낮아 브랜드가 답변에 빠진다」를 발견하면 올린다.
 * geo 스레드 알림의 링크가 상세로 온다. **이 화면 하나로 인터뷰 준비부터 프로필 수정까지 끝낸다:**
 *   ① 노출 안 된 ChatGPT 답변 ② 왜 빠졌나 ③ 인용된 페이지의 순위 산정표 ④ 인터뷰에서 물을 것 ⑤ 프로필 업데이트
 *
 * ★ 에이전트는 산식을 바꾸지 않는다 — 프로필(고객이 준 최신 사실)을 고치면 다음 에이전트 실행이 **기존 산식으로** 반영한다.
 * ★ 운영자 전용이다 — 경쟁 업체 점수표가 실린다.
 */
import { geoCase, metrics, registry } from '@geo-admin/shared/contract/api/index.ts';
import type { GeoCaseDetail, GeoCaseRow } from '@geo-admin/shared/contract/api/geoCase.ts';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { BrandInfoAdmin } from '../components/BrandInfoAdmin.tsx';
import { MentionSnippet } from '../components/MentionSnippet.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

function StatusBadge({ c }: { c: GeoCaseRow }) {
  if (c.status === 'applied') return <Badge tone="green">반영됨</Badge>;
  if (c.status === 'dismissed') return <Badge>닫음</Badge>;
  return c.profileUpdatedAfter ? (
    <Badge tone="blue">프로필 갱신됨 · 반영 대기</Badge>
  ) : (
    <Badge tone="amber">인터뷰 필요</Badge>
  );
}

export function GeoCasesPage() {
  const list = useApi(geoCase.listGeoCases, { query: {} });
  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorBox error={list.error} />;
  const cases = list.data!.cases;
  const open = cases.filter((c) => c.status === 'open').length;
  return (
    <>
      <PageTitle title="인터뷰 준비" description={`열린 케이스 ${open}건 · 개선 에이전트가 올린다`} />
      <Card>
        <CardBody className="p-0">
          {cases.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">케이스가 없습니다.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="p-3">브랜드 · 질문</th>
                  <th className="p-3">인용된 페이지</th>
                  <th className="p-3">순위</th>
                  <th className="p-3">상태</th>
                  <th className="p-3">갱신</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="p-3">
                      <Link to="/admin/geo-cases/$id" params={{ id: c.id }} className="font-medium hover:underline">
                        {c.brandName}
                      </Link>
                      <div className="text-xs text-slate-500">{c.query}</div>
                    </td>
                    <td className="max-w-xs truncate p-3 text-xs">{c.pageUrl}</td>
                    <td className="p-3">{c.rank !== null ? `${c.rank}위 / ${c.candidates}곳` : '—'}</td>
                    <td className="p-3">
                      <StatusBadge c={c} />
                    </td>
                    <td className="p-3 text-xs text-slate-500">{dateTime(c.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </>
  );
}

export function GeoCaseDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useApi(geoCase.getGeoCase, { params: { id } });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const c = q.data!.case;
  return (
    <>
      <PageTitle
        title={`${c.brandName} — 「${c.query}」`}
        description={`인용된 페이지 ${c.pageUrl} · 올린 날 ${dateTime(c.createdAt)}`}
        actions={<StatusBadge c={c} />}
      />
      {c.status !== 'open' && c.resolvedNote ? (
        <Card>
          <CardHeader
            title={c.status === 'applied' ? '반영 결과' : '닫은 이유'}
            description={c.resolvedAt ? dateTime(c.resolvedAt) : undefined}
          />
          <CardBody className="whitespace-pre-wrap text-sm">{c.resolvedNote}</CardBody>
        </Card>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="① 브랜드가 빠진 ChatGPT 답변"
              description="우리 페이지를 인용했는데 브랜드가 답변에 없는 최근 샘플입니다 · 초록 = 우리 사이트 인용"
            />
            <CardBody className="space-y-4">
              {c.sampleIds.slice(0, 3).map((sid) => (
                <SampleAnswer key={sid} id={sid} />
              ))}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="② 왜 빠졌나" description="개선 에이전트의 분석입니다" />
            <CardBody className="whitespace-pre-wrap text-sm leading-relaxed">{c.analysisMd}</CardBody>
          </Card>
        </div>
        <div className="space-y-4">
          <RankingTable c={c} />
          <Card>
            <CardHeader
              title="④ 인터뷰에서 물을 것"
              description="점수가 오르는 경계에서 만든 질문입니다 · 고객이 확인해 준 값만 프로필에 적습니다"
            />
            <CardBody>
              {c.questions.length === 0 ? (
                <p className="text-sm text-slate-500">자동으로 만든 질문이 없습니다. 분석을 보고 물을 것을 정합니다.</p>
              ) : (
                <ol className="list-decimal space-y-2 pl-5 text-sm">
                  {c.questions.map((x) => (
                    <li key={`${x.part}:${x.question}`}>
                      <div className="font-medium">{x.question}</div>
                      <div className="text-xs text-slate-500">
                        지금 {x.current} · {x.gain}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
      <ProfileEditor brandId={c.brandId} />
      {c.status === 'open' ? <Dismiss id={c.id} /> : null}
    </>
  );
}

function SampleAnswer({ id }: { id: string }) {
  const s = useApi(metrics.sample, { params: { id } });
  if (s.isLoading) return <Loading />;
  if (s.error) return <ErrorBox error={s.error} />;
  const v = s.data!;
  const ourNames = v.citations.filter((x) => x.siteUrlPrefix).flatMap((x) => (x.attribution ? [x.attribution] : []));
  return (
    <div className="space-y-1 border-b border-slate-100 pb-3 last:border-0">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/admin/samples/$id" params={{ id }} className="hover:underline">
          {dateTime(v.createdAt)}
        </Link>
        {v.entities.length > 0 ? <span>장소 카드: {v.entities.map((e) => e.name).join(', ')}</span> : null}
      </div>
      <details>
        <summary className="cursor-pointer text-sm">{(v.answerText ?? '').slice(0, 120)}…</summary>
        <div className="mt-2">
          <MentionSnippet text={v.answerText ?? ''} snippets={v.mention.snippets} ourChips={ourNames} />
        </div>
      </details>
    </div>
  );
}

function RankingTable({ c }: { c: GeoCaseDetail }) {
  const t = c.rankingTable;
  if (!t)
    return (
      <Card>
        <CardHeader title="③ 인용된 페이지의 순위 산정표" />
        <CardBody className="text-sm text-slate-500">
          이 사이트는 아직 산정표를 자동으로 만들지 못합니다. 페이지를 직접 봅니다.
        </CardBody>
      </Card>
    );
  return (
    <Card>
      <CardHeader
        title="③ 인용된 페이지의 순위 산정표"
        description={`${t.pageTitle} · 후보 ${t.candidates}곳 · 산식 ${t.formula} · ${t.asOf} 기준`}
      />
      <CardBody className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="p-2">순위</th>
              <th className="p-2">이름</th>
              {t.columns.map((col) => (
                <th key={col.key} className="p-2 text-right">
                  {col.label}
                  <span className="text-slate-400">/{col.max}</span>
                </th>
              ))}
              <th className="p-2 text-right">총점</th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr
                key={`${r.rank}:${r.name}`}
                className={r.ours ? 'bg-amber-50 font-medium' : 'border-t border-slate-100'}
              >
                <td className="p-2">{r.rank}</td>
                <td className="p-2">{r.name}</td>
                {t.columns.map((col) => (
                  <td key={col.key} className="p-2 text-right tabular-nums">
                    {r.parts[col.key] ?? '—'}
                  </td>
                ))}
                <td className="p-2 text-right tabular-nums">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

/** ⑤ — 브랜드 상세의 카드를 그대로 쓴다. 저장하면 profileUpdatedAt 이 움직이고 상태 배지가 「프로필 갱신됨」으로 바뀐다 */
function ProfileEditor({ brandId }: { brandId: string }) {
  const b = useApi(registry.getBrand, { params: { id: brandId } });
  if (b.isLoading) return <Loading />;
  if (b.error) return <ErrorBox error={b.error} />;
  return (
    <div className="space-y-2">
      <h2 className="font-semibold">⑤ 고객 프로필 업데이트</h2>
      <p className="text-sm text-slate-500">
        인터뷰에서 확인한 값을 「브랜드 프로필」에 적고 저장합니다. 다음 에이전트 실행이 기존 산식으로 순위에 반영하고
        결과를 geo 스레드에 남깁니다.
      </p>
      <BrandInfoAdmin brand={b.data!.brand} />
    </div>
  );
}

function Dismiss({ id }: { id: string }) {
  const [note, setNote] = useState('');
  const m = useApiMutation(geoCase.dismissGeoCase);
  return (
    <Card>
      <CardHeader title="케이스 닫기" description="인터뷰가 필요 없거나 할 수 없을 때만 닫습니다" />
      <CardBody className="space-y-2">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="닫는 이유" rows={2} />
        <Button
          size="sm"
          variant="outline"
          disabled={!note.trim() || m.isPending}
          onClick={() => m.mutate({ params: { id }, body: { note } })}
        >
          닫기
        </Button>
        {m.error ? <ErrorBox error={m.error} /> : null}
      </CardBody>
    </Card>
  );
}
